(function (global) {
  'use strict';

  var CM_PER_INCH = 2.54;
  var MAX_DIMENSION = 100000;

  function round(value) {
    return Math.round(value);
  }

  function validateDimension(value) {
    if (value === '' || value === null || typeof value === 'undefined') return false;
    var number = Number(value);
    return Number.isFinite(number) && number > 0 && number <= MAX_DIMENSION;
  }

  function toInches(value, unit) {
    var number = Number(value);
    if (unit === 'ft') return number * 12;
    if (unit === 'cm') return number / CM_PER_INCH;
    return number;
  }

  function inchesToCentimeters(range) {
    return range.map(function (value) {
      return round(value * CM_PER_INCH);
    });
  }

  function calculateRoom(input) {
    var lengthFeet = toInches(input.length, input.unit) / 12;
    var widthFeet = toInches(input.width, input.unit) / 12;
    var estimate = lengthFeet + widthFeet;
    var diameter = [Math.max(8, round(estimate - 2)), round(estimate + 2)];

    return {
      diameter: diameter,
      diameterCm: inchesToCentimeters(diameter),
      formula: round(lengthFeet * 10) / 10 + ' + ' + round(widthFeet * 10) / 10 + ' = ' + round(estimate) + ' in',
    };
  }

  function calculateTable(input) {
    var widthInches = toInches(input.width, input.unit);
    var lengthInches = toInches(input.length, input.unit);
    var width = [round(widthInches * 0.5), round(widthInches * (2 / 3))];
    var length = input.shape === 'linear'
      ? [round(lengthInches * 0.5), round(lengthInches * (2 / 3))]
      : null;

    return {
      width: width,
      widthCm: inchesToCentimeters(width),
      length: length,
      lengthCm: length ? inchesToCentimeters(length) : null,
      formula: '50%–67% of the table dimensions',
    };
  }

  function calculateHangingHeight(input) {
    var ceilingFeet = toInches(input.ceilingHeight, input.unit) / 12;
    var lowCeiling = ceilingFeet < 8;
    var adjustment = lowCeiling ? 0 : Math.floor(ceilingFeet - 8) * 3;
    var inches = [30 + adjustment, 36 + adjustment];

    return {
      inches: inches,
      centimeters: inchesToCentimeters(inches),
      lowCeiling: lowCeiling,
    };
  }

  function selectedValue(root, name) {
    var selected = root.querySelector('input[name="' + name + '"]:checked');
    return selected ? selected.value : '';
  }

  function formatRange(range, unit) {
    return range[0] + '–' + range[1] + ' ' + unit;
  }

  function setFieldState(root, input, valid) {
    var error = root.querySelector('[data-error-for="' + input.dataset.field + '"]');
    input.setAttribute('aria-invalid', valid ? 'false' : 'true');
    if (error) error.hidden = valid;
  }

  function updateUnitLabels(root) {
    var metric = selectedValue(root, 'calculator-units') === 'metric';
    root.querySelectorAll('[data-unit-label]').forEach(function (label) {
      label.textContent = metric ? 'cm' : label.dataset.imperialUnit;
    });
  }

  function updateMode(root) {
    var mode = selectedValue(root, 'calculator-mode') || 'room';
    root.querySelectorAll('[data-mode-fields]').forEach(function (group) {
      var active = group.dataset.modeFields === mode;
      group.hidden = !active;
      group.querySelectorAll('input').forEach(function (input) {
        input.disabled = !active;
      });
    });
    var shape = root.querySelector('[data-shape-control]');
    if (shape) shape.hidden = mode !== 'table';
  }

  function renderResult(root, payload) {
    var output = root.querySelector('[data-calculator-output]');
    var secondary = output.querySelector('[data-result-secondary]');
    var height = output.querySelector('[data-result-height]');
    var warning = output.querySelector('[data-result-warning]');

    output.querySelector('[data-result-primary]').textContent = payload.primary;
    output.querySelector('[data-result-formula]').textContent = payload.formula;
    secondary.textContent = payload.secondary || '';
    secondary.hidden = !payload.secondary;
    height.textContent = payload.height || '';
    height.hidden = !payload.height;
    warning.textContent = payload.warning || '';
    warning.hidden = !payload.warning;
    output.hidden = false;
  }

  function initialize(root) {
    if (!root || root.dataset.calculatorMounted === 'true') return;
    root.dataset.calculatorMounted = 'true';

    var form = root.querySelector('[data-calculator-form]');
    if (!form) return;

    root.querySelectorAll('input[name="calculator-mode"]').forEach(function (input) {
      input.addEventListener('change', function () {
        updateMode(root);
        updateUnitLabels(root);
      });
    });
    root.querySelectorAll('input[name="calculator-units"]').forEach(function (input) {
      input.addEventListener('change', function () {
        updateUnitLabels(root);
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var mode = selectedValue(root, 'calculator-mode') || 'room';
      var metric = selectedValue(root, 'calculator-units') === 'metric';
      var shape = selectedValue(root, 'calculator-shape') || 'round';
      var fields = mode === 'room'
        ? ['room-length', 'room-width']
        : ['table-length', 'table-width'];
      var valid = true;

      fields.forEach(function (field) {
        var input = root.querySelector('[data-field="' + field + '"]');
        var fieldValid = validateDimension(input.value);
        setFieldState(root, input, fieldValid);
        valid = valid && fieldValid;
      });

      var ceiling = root.querySelector('[data-field="ceiling-height-' + mode + '"]');
      if (ceiling && ceiling.value !== '') {
        var ceilingValid = validateDimension(ceiling.value);
        setFieldState(root, ceiling, ceilingValid);
        valid = valid && ceilingValid;
      }

      var summary = root.querySelector('[data-error-summary]');
      summary.hidden = valid;
      if (!valid) return;

      var payload;
      if (mode === 'room') {
        var roomResult = calculateRoom({
          length: root.querySelector('[data-field="room-length"]').value,
          width: root.querySelector('[data-field="room-width"]').value,
          unit: metric ? 'cm' : 'ft',
        });
        payload = {
          primary: 'Fixture diameter: ' + formatRange(metric ? roomResult.diameterCm : roomResult.diameter, metric ? 'cm' : 'in'),
          formula: roomResult.formula,
          warning: ceiling && ceiling.value !== ''
            ? 'Open-room clearance depends on the full fixture drop, walking paths and the exact installation. Verify these separately.'
            : '',
        };
      } else {
        var tableResult = calculateTable({
          length: root.querySelector('[data-field="table-length"]').value,
          width: root.querySelector('[data-field="table-width"]').value,
          unit: metric ? 'cm' : 'in',
          shape: shape,
        });
        payload = {
          primary: 'Fixture width: ' + formatRange(metric ? tableResult.widthCm : tableResult.width, metric ? 'cm' : 'in'),
          secondary: tableResult.length
            ? 'Linear fixture length: ' + formatRange(metric ? tableResult.lengthCm : tableResult.length, metric ? 'cm' : 'in')
            : '',
          formula: tableResult.formula,
        };

        if (ceiling && ceiling.value !== '') {
          var hanging = calculateHangingHeight({
            ceilingHeight: ceiling.value,
            unit: metric ? 'cm' : 'ft',
          });
          payload.height = 'Lowest point above tabletop: ' + formatRange(metric ? hanging.centimeters : hanging.inches, metric ? 'cm' : 'in');
          payload.warning = hanging.lowCeiling
            ? 'Low ceiling: do not reduce the standard tabletop range automatically. Verify total drop, sightlines and clearance with the installer.'
            : '';
        }
      }

      renderResult(root, payload);
      root.dispatchEvent(new CustomEvent('casa:chandelier-calculation', {
        bubbles: true,
        detail: { mode: mode, units: metric ? 'metric' : 'imperial', shape: shape },
      }));
    });

    updateMode(root);
    updateUnitLabels(root);
  }

  var api = {
    calculateRoom: calculateRoom,
    calculateTable: calculateTable,
    calculateHangingHeight: calculateHangingHeight,
    validateDimension: validateDimension,
    initialize: initialize,
  };

  global.CasaChandelierCalculator = api;

  if (typeof document !== 'undefined') {
    function initializeAll(scope) {
      var target = scope && scope.querySelectorAll ? scope : document;
      target.querySelectorAll('[data-chandelier-calculator]').forEach(initialize);
      if (target.matches && target.matches('[data-chandelier-calculator]')) initialize(target);
    }

    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    });
    document.addEventListener('shopify:section:load', function (event) {
      initializeAll(event.target);
    });
  }
})(typeof globalThis !== 'undefined' ? globalThis : window);
